import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, Dialog, Icon } from '@/ds';
import { EmptyState } from '@/components/EmptyState';
import { Money, Provenance, Rate } from '@/components/money';
import { useOperation, useWallet } from '@/api/hooks';
import { formatDate, formatMoney, formatNumber, formatRelative } from '@/lib/format';
import { useIsMobile } from '@/lib/useMediaQuery';
import { WalletTopUpDialog } from './wallets/WalletTopUpDialog';
import { WalletFormDialog } from './wallets/WalletFormDialog';
import {
  KIND_LABELS,
  WalletBalance,
  rateSourceLabel,
  ratePolicyText,
  recordSourceLabel,
} from './wallets/walletView';
import { ErrorState, Loading } from '@/components/QueryState';
import { WalletOperationsCard } from './wallets/WalletOperationsCard';
import { ApiError } from '@/api/client';

function feeText(feeKind: string, feeValue: number, currency: string): string {
  if (feeKind === 'none' || feeValue === 0) return '—';
  if (feeKind === 'percent') {
    // 2%, а не 2,00%: лишние нули только мешают читать.
    const digits = feeValue % 100 === 0 ? 0 : 1;
    return `${formatNumber(feeValue / 100, digits)}%`;
  }
  return formatMoney(feeValue, currency);
}

export function WalletDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const wallet = useWallet(id);
  const [toppingUp, setToppingUp] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deletingTopUp, setDeletingTopUp] = useState<string | null>(null);

  const removeTopUp = useOperation<{ id: string }>('wallet_topup.delete');

  if (wallet.isLoading) {
    return (
      <div className="kj-page">
        <Loading what="кошелёк" />
      </div>
    );
  }

  // Сетевой сбой и удалённый кошелёк — разные вещи, и говорить о них надо разное.
  const missing =
    wallet.error instanceof ApiError && (wallet.error.status === 404 || wallet.error.status === 400);

  if (wallet.isError && !missing) {
    return (
      <div className="kj-page">
        <ErrorState
          what="кошелёк"
          error={wallet.error}
          onRetry={() => void wallet.refetch()}
          retrying={wallet.isFetching}
        />
      </div>
    );
  }

  if (wallet.isError || !wallet.data) {
    return (
      <div className="kj-page">
        <EmptyState
          icon="CreditCard"
          title="Кошелёк не найден"
          description="Возможно, он был удалён. Вернитесь к списку и выберите другой."
          action={
            <Button variant="secondary" onClick={() => navigate('/wallets')}>
              К списку кошельков
            </Button>
          }
        />
      </div>
    );
  }

  const data = wallet.data;
  const showTopUps = data.manualTopUps || data.topUps.length > 0;

  return (
    <div className="kj-page">
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/wallets')}
          iconLeft={<Icon name="ArrowLeft" size={16} />}
        >
          Все кошельки
        </Button>
      </div>

      <Card padding="lg">
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: 'var(--space-6)',
          }}
        >
          <div style={{ flex: 1, minWidth: 220 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-4)',
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  font: '500 24px var(--font-display)',
                  letterSpacing: 'var(--ls-heading)',
                  color: 'var(--text-strong)',
                }}
              >
                {data.name}
              </span>
              {data.isActive ? null : <Badge tone="neutral">Отключён</Badge>}
            </div>
            <div
              style={{
                marginTop: 'var(--space-3)',
                font: '400 13px var(--font-text)',
                color: 'var(--text-subtle)',
              }}
            >
              {KIND_LABELS[data.kind] ?? data.kind} · {data.currency}
            </div>
            {data.sourceTitle ? (
              <div style={{ marginTop: 'var(--space-3)' }}>
                <Provenance>{data.sourceTitle}</Provenance>
              </div>
            ) : null}
            {data.sync ? (
              <div style={{ marginTop: 'var(--space-2)' }}>
                <Provenance>
                  {data.sync.lastSyncAt
                    ? `${data.sync.source === 'telegram_forward' ? 'Последнее пересланное сообщение' : 'Последний сбор'} ${formatRelative(data.sync.lastSyncAt)}` +
                      (data.sync.status === 'failed' ? ' — с ошибкой' : '')
                    : data.sync.source === 'telegram_forward'
                      ? 'Сообщений кошелька ещё не пересылали'
                      : 'Из источника ещё не собирался'}
                  {data.sync.error ? `. ${data.sync.error}` : ''}
                </Provenance>
              </div>
            ) : null}
            {data.notes ? (
              <p
                style={{
                  marginTop: 'var(--space-4)',
                  font: '400 13px/1.6 var(--font-text)',
                  color: 'var(--text-muted)',
                  maxWidth: 520,
                }}
              >
                {data.notes}
              </p>
            ) : null}
          </div>

          <div style={{ minWidth: 200, maxWidth: 360 }}>
            <WalletBalance wallet={data} size="xl" />
            <div style={{ marginTop: 'var(--space-3)' }}>
              <Provenance>{ratePolicyText(data)}</Provenance>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            {data.manualTopUps ? (
              <Button onClick={() => setToppingUp(true)} iconLeft={<Icon name="Plus" size={16} />}>
                Пополнить
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => setEditing(true)}>
              Изменить
            </Button>
          </div>
        </div>
      </Card>

      {showTopUps ? (
      <Card padding={isMobile ? 'sm' : 'md'}>
        <div
          style={{
            font: '500 16px var(--font-display)',
            letterSpacing: 'var(--ls-heading)',
            color: 'var(--text-strong)',
            marginBottom: 'var(--space-5)',
          }}
        >
          История пополнений
        </div>

        {data.topUps.length === 0 ? (
          <div style={{ font: '400 13px var(--font-text)', color: 'var(--text-subtle)' }}>
            Пополнений ещё не было. Нажмите «Пополнить», чтобы внести первое.
          </div>
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {data.topUps.map((row) => (
              <Card key={row.id} tone="sunken" padding="sm" elevation="none">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <Money minor={row.creditedAmount} currency={data.currency} />
                  <span className="kj-mono" style={{ fontSize: 'var(--fs-2xs)' }}>
                    {formatDate(row.occurredAt)}
                  </span>
                </div>
                <div style={{ marginTop: 'var(--space-3)' }}>
                  <Provenance>
                    Списано {formatMoney(row.paidAmount, row.paidCurrency)} · курс{' '}
                    {row.effectiveRate === null ? '—' : formatNumber(row.effectiveRate, 2)} · комиссия{' '}
                    {feeText(row.feeKind, row.feeValue, row.paidCurrency)}
                  </Provenance>
                  {row.author ? <Provenance>Внёс {row.author}</Provenance> : null}
                  {row.comment ? <Provenance>{row.comment}</Provenance> : null}
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="kj-scroll-x">
            <table className="kj-table">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Зачислено</th>
                  <th>Списано</th>
                  <th>Комиссия</th>
                  <th>Курс</th>
                  <th>Кто</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.topUps.map((row) => (
                  <tr key={row.id}>
                    <td data-label="Дата" className="kj-mono">{formatDate(row.occurredAt)}</td>
                    <td data-label="Зачислено">
                      <Money minor={row.creditedAmount} currency={data.currency} size="sm" />
                    </td>
                    <td data-label="Списано">
                      <Money minor={row.paidAmount} currency={row.paidCurrency} size="sm" />
                    </td>
                    <td data-label="Комиссия" className="kj-mono">
                      {feeText(row.feeKind, row.feeValue, row.paidCurrency)}
                    </td>
                    <td data-label="Курс">
                      <Rate rate={row.effectiveRate} />
                    </td>
                    <td data-label="Кто">{row.author ?? '—'}</td>
                    <td data-label="" style={{ textAlign: 'right' }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeletingTopUp(row.id)}
                      >
                        Удалить
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      ) : data.topUpsNote ? (
        <Provenance>{data.topUpsNote}</Provenance>
      ) : null}

      <Card padding={isMobile ? 'sm' : 'md'}>
        <div
          style={{
            font: '500 16px var(--font-display)',
            letterSpacing: 'var(--ls-heading)',
            color: 'var(--text-strong)',
            marginBottom: 'var(--space-5)',
          }}
        >
          Платежи в сервисы
        </div>

        {data.payments.length === 0 ? (
          <div style={{ font: '400 13px var(--font-text)', color: 'var(--text-subtle)' }}>
            С этого кошелька ещё ничего не платили.
          </div>
        ) : (
          <div className="kj-scroll-x">
            <table className="kj-table">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Сервис</th>
                  <th>Сумма</th>
                  <th>Комиссия</th>
                  <th>Себестоимость</th>
                  <th>Откуда</th>
                  <th>Кто</th>
                </tr>
              </thead>
              <tbody>
                {data.payments.map((row) => (
                  <tr key={row.id}>
                    <td data-label="Дата" className="kj-mono">{formatDate(row.occurredAt)}</td>
                    <td data-label="Сервис" style={{ color: 'var(--text-strong)' }}>{row.serviceName}</td>
                    <td data-label="Сумма">
                      <Money minor={row.amount} currency={data.currency} size="sm" />
                    </td>
                    <td data-label="Комиссия">
                      {row.fee > 0 ? (
                        <Money minor={row.fee} currency={data.currency} size="sm" tone="muted" />
                      ) : (
                        <span style={{ color: 'var(--text-subtle)' }}>—</span>
                      )}
                    </td>
                    <td data-label="Себестоимость">
                      <Money minor={row.rubCost} currency="RUB" size="sm" tone="muted" />
                      {row.rubCost !== null ? (
                        <Provenance>{rateSourceLabel(row.rateSource)}</Provenance>
                      ) : (
                        <Provenance>курс не найден</Provenance>
                      )}
                    </td>
                    <td data-label="Откуда" style={{ color: 'var(--text-muted)' }}>
                      {recordSourceLabel(row.source)}
                    </td>
                    <td data-label="Кто">{row.author ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {data.operations.length > 0 ? <WalletOperationsCard wallet={data} /> : null}

      {data.expenses.length > 0 ? (
        <Card padding={isMobile ? 'sm' : 'md'}>
          <div
            style={{
              font: '500 16px var(--font-display)',
              letterSpacing: 'var(--ls-heading)',
              color: 'var(--text-strong)',
              marginBottom: 'var(--space-5)',
            }}
          >
            Прочие расходы с кошелька
          </div>
          <div className="kj-scroll-x">
            <table className="kj-table">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>За что</th>
                  <th>Сумма</th>
                  <th>Себестоимость</th>
                </tr>
              </thead>
              <tbody>
                {data.expenses.map((row) => (
                  <tr key={row.id}>
                    <td data-label="Дата" className="kj-mono">{formatDate(row.date)}</td>
                    <td data-label="За что" style={{ color: 'var(--text-strong)' }}>{row.comment}</td>
                    <td data-label="Сумма">
                      <Money minor={row.amount} currency={row.currency} size="sm" />
                    </td>
                    <td data-label="Себестоимость">
                      <Money minor={row.rubCost} currency="RUB" size="sm" tone="muted" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      <WalletTopUpDialog wallet={data} open={toppingUp} onClose={() => setToppingUp(false)} />
      <WalletFormDialog wallet={data} open={editing} onClose={() => setEditing(false)} />

      <Dialog
        open={deletingTopUp !== null}
        title="Удалить пополнение"
        description="Остаток кошелька и средневзвешенный курс пересчитаются. Запись останется в журнале действий."
        onClose={() => setDeletingTopUp(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeletingTopUp(null)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              loading={removeTopUp.isPending}
              onClick={() => {
                if (!deletingTopUp) return;
                removeTopUp.mutate(
                  { id: deletingTopUp },
                  { onSuccess: () => setDeletingTopUp(null) },
                );
              }}
            >
              Удалить
            </Button>
          </>
        }
      />
    </div>
  );
}
